// Obra › Clima › Pronóstico: 48 h por hora (cielo animado de fondo, temperatura, lluvia y viento) y 16 días con
// semáforo de trabajo (✅ apto / ⚠️ con cuidado / ⛔ no apto) y el porqué.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine } from 'recharts';
import R from './reglasClima';
import { SkyDefs, SkyBand, WxIcon, SKY_CSS } from '../../IADashboard/clima/Sky';
import { Seccion, Semaforo, Icono3D, ESTADOS, mm, grados, nivelLluvia, diaCorto } from './ui';

const useAncho = () => {
  const ref = useRef(null);
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

function Tip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white/95 border border-gray-200 rounded-xl shadow-lg px-3 py-2 text-xs text-gray-700 space-y-0.5">
      <p className="font-semibold text-gray-900">{diaCorto(d.t.slice(0, 10))} · {d.h} h</p>
      <p>{R.TIEMPO_TXT[d.kind]} · {grados(d.temp)} (sensación {grados(d.sens)})</p>
      <p>Lluvia {mm(d.mm)} mm · probabilidad {d.prob ?? 0} %</p>
      <p>Viento {Math.round(d.viento ?? 0)} km/h · ráfagas {Math.round(d.rafaga ?? 0)} km/h {R.direccionTxt(d.dir)}</p>
    </div>
  );
}

function Grafico48({ ix, U, desde }) {
  const [ref, ancho] = useAncho();
  const uid = 'p48';
  const data = useMemo(() => ix.horas.filter((x) => x.t >= desde).slice(0, 48).map((x, i) => ({
    ...x, idx: i, kind: R.tipoTiempo(x.code, x.mm, x.dia === 0) || 'nublado', night: x.dia === 0, mmBar: x.mm >= 0.05 ? x.mm : 0,
  })), [ix, desde]);
  const bands = useMemo(() => {
    const out = [];
    data.forEach((r, i) => {
      const level = nivelLluvia(r.mm, true);
      const last = out[out.length - 1];
      if (last && last.kind === r.kind && last.night === r.night && last.level === level) last.b = i; else out.push({ a: i, b: i, kind: r.kind, night: r.night, level });
    });
    return out;
  }, [data]);
  const paso = ancho < 520 ? 6 : 3;
  const iconos = data.filter((r) => r.idx % paso === 0 || (r.kind === 'tormenta' && r.idx % 2 === 0));
  const medianoche = data.filter((r) => r.h === 0 && r.idx > 0);
  const maxMm = Math.max(2, ...data.map((r) => r.mm));
  const jornadas = [];
  data.forEach((r) => {
    const en = r.h >= U.jornadaDesde && r.h < U.jornadaHasta;
    const last = jornadas[jornadas.length - 1];
    if (en) { if (last && last.b === r.idx - 1) last.b = r.idx; else jornadas.push({ a: r.idx, b: r.idx }); }
  });
  return (
    <div ref={ref}>
      <style>{SKY_CSS}</style>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 34, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
            <defs>
              <SkyDefs uid={uid} />
              <linearGradient id="gTemp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f97316" stopOpacity={0.25} /><stop offset="100%" stopColor="#f97316" stopOpacity={0} /></linearGradient>
            </defs>
            {bands.map((b) => (
              <ReferenceArea key={'s' + b.a} yAxisId="t" x1={b.a} x2={b.b} ifOverflow="extendDomain" zIndex={-50} shape={(p) => <SkyBand {...p} x={p.x - 2} width={p.width + 4} uid={uid} band={b} animate={bands.length < 30} />} />
            ))}
            {jornadas.map((j) => <ReferenceArea key={'j' + j.a} yAxisId="t" x1={j.a} x2={j.b} fill="none" stroke="none" label={{ value: 'Horario de obra', position: 'insideBottom', fill: '#475569', fontSize: 9, offset: 4 }} />)}
            <CartesianGrid stroke="#e5e7eb" strokeOpacity={0.6} vertical={false} />
            <XAxis dataKey="idx" type="category" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(i) => (data[i] ? `${data[i].h}h` : '')} interval={paso - 1} stroke="#e5e7eb" />
            <YAxis yAxisId="t" tick={{ fill: '#6b7280', fontSize: 11 }} domain={['dataMin - 3', 'dataMax + 3']} unit="°" width={48} allowDecimals={false} />
            <YAxis yAxisId="mm" orientation="right" domain={[0, maxMm * 2.2]} hide />
            <Tooltip content={<Tip />} cursor={{ fill: 'rgba(37,99,235,.06)' }} isAnimationActive={false} />
            {medianoche.map((r) => <ReferenceLine key={'m' + r.idx} yAxisId="t" x={r.idx} stroke="#334155" strokeDasharray="3 3" strokeOpacity={0.6} label={{ value: diaCorto(r.fecha), position: 'insideTopLeft', fill: '#334155', fontSize: 11, fontWeight: 700 }} />)}
            <Bar yAxisId="mm" dataKey="mmBar" fill="#0284c7" fillOpacity={0.8} radius={[3, 3, 0, 0]} maxBarSize={12} isAnimationActive={false} name="Lluvia" />
            <Area yAxisId="t" dataKey="temp" type="monotone" stroke="#ea580c" strokeWidth={2.5} fill="url(#gTemp)" dot={false} isAnimationActive={false} name="Temperatura" />
            {iconos.map((r) => (
              <ReferenceArea key={'i' + r.idx} yAxisId="t" x1={r.idx} x2={r.idx} fill="none" stroke="none" ifOverflow="extendDomain" label={<WxIcon wx={{ kind: r.kind, label: R.TIEMPO_TXT[r.kind], detail: `${r.h} h · ${grados(r.temp)}` }} size={ancho < 520 ? 20 : 24} />} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {/* probabilidad de lluvia como cinta */}
      <div className="flex gap-px mt-1 ml-[30px] mr-[4px] h-3 rounded overflow-hidden" title="Probabilidad de lluvia">
        {data.map((r) => <span key={r.t} className="flex-1" style={{ background: `rgba(2,132,199,${Math.min(1, (r.prob || 0) / 100) * 0.9 + 0.05})` }} title={`${r.h} h · ${r.prob ?? 0} %`} />)}
      </div>
      <p className="text-[10px] text-gray-500 ml-[30px] mt-0.5">Probabilidad de lluvia por hora (más oscuro = más probable)</p>
      {/* viento */}
      <div style={{ width: '100%', height: 130 }} className="mt-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 4, left: -18, bottom: 0 }} accessibilityLayer={false}>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="idx" type="category" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(i) => (data[i] ? `${data[i].h}h` : '')} interval={paso - 1} stroke="#e5e7eb" />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} width={48} unit="" domain={[0, (max) => Math.max(U.gruaRafaga + 10, max + 5)]} allowDecimals={false} />
            <Tooltip content={<Tip />} isAnimationActive={false} />
            <ReferenceLine y={U.gruaRafaga} stroke="#e11d48" strokeDasharray="5 4" label={{ value: `Límite grúa ${U.gruaRafaga} km/h`, position: 'insideTopRight', fill: '#e11d48', fontSize: 10 }} />
            <Area dataKey="viento" type="monotone" stroke="#64748b" fill="#cbd5e1" fillOpacity={0.5} isAnimationActive={false} name="Viento" />
            <Line dataKey="rafaga" type="monotone" stroke="#7c3aed" strokeWidth={2} dot={false} isAnimationActive={false} name="Ráfagas" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600 mt-1 px-1">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-orange-600 rounded" />Temperatura</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-sky-600 rounded-sm" />Lluvia (mm por hora)</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 bg-slate-300 rounded-sm" />Viento</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-violet-600 rounded" />Ráfagas (km/h)</span>
      </div>
    </div>
  );
}

function DetalleDia({ ix, e, U, hoy, onTareas }) {
  const horas = (ix.porFecha.get(e.fecha) || []).filter((x) => x.h >= 6 && x.h <= 21);
  const tareas = Object.keys(R.TAREAS).map((k) => R.evaluarTarea(k, ix, e.fecha, U)).filter(Boolean);
  const adelante = Math.round((Date.parse(e.fecha) - Date.parse(hoy)) / 864e5);
  return (
    <div className={`rounded-2xl border-2 ${ESTADOS[e.estado]?.borde || 'border-gray-200'} bg-white p-4 clima-in`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-gray-900">{R.mayuscula(R.fechaLarga(e.fecha))}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Semaforo estado={e.estado} />
            {!e.laborable && <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">No se trabaja</span>}
            <span className="text-[11px] text-gray-500">Confianza del pronóstico: {R.confianza(adelante)}</span>
          </div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {e.motivos.length ? e.motivos.map((m) => (
          <li key={m.tipo + m.texto} className="flex items-start gap-2 text-sm text-gray-800"><span aria-hidden="true">{ESTADOS[m.estado].emoji}</span>{m.texto}</li>
        )) : <li className="flex items-start gap-2 text-sm text-gray-800"><span aria-hidden="true">✅</span>Sin lluvia, viento ni temperaturas que compliquen el trabajo entre las {U.jornadaDesde} y las {U.jornadaHasta} h.</li>}
      </ul>
      {horas.length > 0 && (
        <div className="overflow-x-auto clima-hide-scroll mt-3 -mx-1">
          <div className="flex gap-1 px-1 min-w-max">
            {horas.map((x) => {
              const k = R.tipoTiempo(x.code, x.mm, x.dia === 0);
              const trabajo = x.h >= U.jornadaDesde && x.h < U.jornadaHasta;
              return (
                <div key={x.t} className={`w-14 rounded-lg py-1.5 flex flex-col items-center gap-0.5 ${trabajo ? 'bg-gray-50' : 'opacity-60'}`}>
                  <span className="text-[10px] font-semibold text-gray-500">{x.h} h</span>
                  <Icono3D kind={k} size={24} />
                  <span className="text-xs font-bold">{grados(x.temp)}</span>
                  <span className={`text-[10px] ${x.mm >= 0.1 ? 'text-sky-700 font-semibold' : 'text-gray-400'}`}>{x.mm >= 0.1 ? mm(x.mm) : `${x.prob ?? 0}%`}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {tareas.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-semibold text-gray-500 mb-1.5">Para este día</p>
          <div className="flex flex-wrap gap-1.5">
            {tareas.map((t) => (
              <button key={t.tarea} type="button" onClick={() => onTareas(t.tarea)} title={t.explicacion}
                className={`text-xs font-semibold rounded-full px-2.5 py-1 border ${t.nivel === 'ideal' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : t.nivel === 'posible' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-500 line-through decoration-gray-300'}`}>
                {R.TAREAS[t.tarea].emoji} {R.TAREAS[t.tarea].nombre} {t.puntaje}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Pronostico({ ix, U, hoy, onTareas, onUmbrales }) {
  const ahoraT = R.localDe(Date.now(), ix.offset).slice(0, 13) + ':00';
  const dias = useMemo(() => ix.dias.filter((d) => d.fecha >= hoy).slice(0, 16).map((d) => ({ d, e: R.evaluarDia(ix, d.fecha, U) })), [ix, hoy, U]);
  const [sel, setSel] = useState(null);
  const elegido = dias.find((x) => x.d.fecha === sel);
  const hab = dias.filter((x) => x.e.laborable && x.e.estado);
  const cuenta = (k) => hab.filter((x) => x.e.estado === k).length;
  return (
    <div className="space-y-4 clima-in">
      <Seccion titulo="Próximas 48 horas" sub="Pasá el dedo o el mouse por el gráfico para ver cada hora">
        <Grafico48 ix={ix} U={U} desde={ahoraT} />
      </Seccion>

      <Seccion
        titulo="Los próximos 16 días para trabajar"
        sub={`Semáforo para el horario de obra (${U.jornadaDesde} a ${U.jornadaHasta} h). Tocá un día para ver el porqué.`}
        right={<button type="button" onClick={onUmbrales} className="text-xs font-semibold text-blue-700 hover:underline">Ajustar criterios</button>}
      >
        <div className="flex flex-wrap gap-2 mb-3">
          <Semaforo estado="apto">{cuenta('apto')} aptos</Semaforo>
          <Semaforo estado="cuidado">{cuenta('cuidado')} con cuidado</Semaforo>
          <Semaforo estado="no">{cuenta('no')} no aptos</Semaforo>
          <span className="text-xs text-gray-500 self-center">de {hab.length} días hábiles</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2">
          {dias.map(({ d, e }, i) => {
            const k = R.tipoTiempo(d.code, d.mm, false) || 'nublado';
            const E = ESTADOS[e.estado] || {};
            const lejos = i >= 8;
            const deNoche = e.estado === 'apto' && e.datos && e.datos.mmDia >= 2 && e.datos.mmJ < U.lluviaCuidado;
            return (
              <button key={d.fecha} type="button" onClick={() => setSel(sel === d.fecha ? null : d.fecha)} aria-pressed={sel === d.fecha}
                className={`relative text-left rounded-2xl border bg-white px-2.5 pt-3 pb-2.5 flex flex-col gap-1 transition-all hover:shadow-md ${sel === d.fecha ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'} ${!e.laborable ? 'opacity-70' : ''}`}>
                <span className="absolute inset-x-0 top-0 h-1.5 rounded-t-2xl" style={{ background: e.laborable ? E.hex || '#e5e7eb' : '#d1d5db' }} />
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-bold text-gray-900 capitalize">{i === 0 ? 'Hoy' : i === 1 ? 'Mañana' : diaCorto(d.fecha)}</span>
                  <span aria-hidden="true" className={`text-sm ${e.laborable ? '' : 'opacity-40 grayscale'}`}>{E.emoji}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Icono3D kind={k} size={36} />
                  <div className="leading-tight">
                    <p className="text-sm font-bold text-gray-900">{grados(d.tmax)}</p>
                    <p className="text-xs text-gray-500">{grados(d.tmin)}</p>
                  </div>
                </div>
                <p className={`text-[11px] ${d.mm >= 1 ? 'text-sky-700 font-semibold' : 'text-gray-500'}`}>💧 {mm(d.mm)} mm{d.prob != null ? ` · ${d.prob}%` : ''}</p>
                <p className={`text-[11px] ${d.rafaga >= U.rafagaCuidado ? 'text-violet-700 font-semibold' : 'text-gray-500'}`}>💨 {Math.round(d.rafaga ?? 0)} km/h</p>
                <p className="text-[11px] text-gray-700 leading-snug line-clamp-2 min-h-[2.3em]">{e.laborable ? (e.estado === 'apto' ? (deNoche ? 'Buen día · llueve fuera del horario' : 'Buen día') : e.motivos[0]?.texto) : 'No se trabaja'}</p>
                {lejos && <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wide">Tendencia</span>}
              </button>
            );
          })}
        </div>
        {elegido && <div className="mt-3"><DetalleDia ix={ix} e={elegido.e} U={U} hoy={hoy} onTareas={onTareas} /></div>}
        <p className="text-[11px] text-gray-400 mt-3">Del día 8 en adelante el pronóstico es una tendencia: puede cambiar. Se actualiza cada vez que abrís esta pantalla.</p>
      </Seccion>
    </div>
  );
}
